import { FC, useMemo, useRef, useEffect } from 'react'
import { FutureLayerUID, FutureLayer } from 'components/features/future/types'
import { FuturePanePreset } from 'components/features/future/FuturePanePreset/FuturePanePreset'
import * as yup from 'yup'
import { useLayerSelector, extendCommonSchema } from 'components/features/future/helpers'
import { useFormContext } from 'react-hook-form'
import { FutureWhenInput, FULL_OPTIONS } from 'components/features/future/FutureWhenInput/FutureWhenInput'
import { FutureFieldSlider } from 'components/features/future/FutureField/FutureFieldSlider'
import { schemaNumber, schemaNumberInString } from 'schemas/baseSchema'
import { maskCurrencyPositive, maskPercentFromZero, maskNumberPositive } from 'components/atoms/inputs/maskedInput'
import { FutureFieldButtons } from 'components/features/future/FutureField/FutureFieldButtons'
import { InputTouchedState } from 'components/atoms/inputs/InputTouchedState/InputTouchedState'
import { useStateRef } from 'hooks/stateHooks'
import { useCollectLayerErrors } from 'components/features/future/hooks/useCollectErrors'
import { ObjectSchema } from 'yup'
import { LayerStatus } from 'components/features/future/LayerStatus/LayerStatus'

const LAYER_SLUG = 'buy_car' as const
type LayerSlugType = typeof LAYER_SLUG
type LayerType = Extract<FutureLayer, { slug: LayerSlugType }>

const DEFAULT_LOAN_PERIOD_MONTHS = 36
const DEFAULT_INTEREST_RATE = '7.00'

const schemaCreator = extendCommonSchema<LayerSlugType>(yup.object({
  purchase_price: schemaNumber,
  down_payment: schemaNumber,
  loan_period_months: schemaNumber,
  interest_rate: schemaNumberInString,
}).transform((values) => {
  if (+values.purchase_price === +values.down_payment) {
    return {
      ...values,
      loan_period_months: 0,
      interest_rate: '0',
    }
  }

  return values
}) as ObjectSchema<LayerType>)

type FutureLayerPaneBuyCarProps = {
  uid?: FutureLayerUID
  onCancel: () => void
}

export const FutureLayerPaneBuyCar: FC<FutureLayerPaneBuyCarProps> = (props) => {
  const { onCancel, uid } = props
  const layer = useLayerSelector<LayerSlugType>(props)

  const layerWidthDefaults = useMemo(() => ({
    loan_period_months: DEFAULT_LOAN_PERIOD_MONTHS,
    interest_rate: DEFAULT_INTEREST_RATE,
    ...layer,
  } as typeof layer), [layer])

  return (
    <FuturePanePreset<LayerSlugType>
      layer={ layerWidthDefaults }
      slug={ LAYER_SLUG }
      name='Buy Car'
      icon='misc-car'
      type='goal'
      schemaCreator={ schemaCreator }
      onCancel={ onCancel }
    >
      <FutureLayerPaneBuyCarForm uid={ uid } />
    </FuturePanePreset>
  )
}

type FutureLayerPaneBuyCarFormProps = {
  uid?: FutureLayerUID
}
const FutureLayerPaneBuyCarForm: FC<FutureLayerPaneBuyCarFormProps> = (props) => {
  const { uid } = props
  const { watch, setValue, getValues } = useFormContext<LayerType>()
  const setValueRef = useRef(setValue)
  setValueRef.current = setValue
  const getValuesRef = useRef(getValues)
  getValuesRef.current = getValues

  const errors = useCollectLayerErrors<LayerType>()

  const [downPaymentTouchedRef, setDownPaymentTouched] = useStateRef(!!uid)

  const purchasePrice = watch('purchase_price')
  const downPayment = watch('down_payment')
  useEffect(() => {
    if (downPayment > purchasePrice) {
      setDownPaymentTouched(false)
    }
  }, [purchasePrice, downPayment, setDownPaymentTouched])

  useEffect(() => {
    if (downPaymentTouchedRef.current) {
      return
    }

    setValueRef.current('down_payment', Math.round(purchasePrice * .2))
  }, [purchasePrice, downPaymentTouchedRef])

  const downPaymentRange = useMemo(() => {
    const max = Math.max(purchasePrice || 0, 0)
    return {
      max,
      step: max / 20,
    }
  }, [purchasePrice])

  const isLoanFieldsDisabled = downPayment === purchasePrice

  const completeChild = useMemo(() => (
    <>
      <h1>
        Goal Achieved. Congratulations!
      </h1>
      Don’t forget to update your Settings:
      <ul>
        <li>Link your new car to add it to your Assets</li>
        <li>Link your auto loan to track your Debts</li>
      </ul>
    </>
  ), [])

  return (
    <LayerStatus uid={ uid || null } completeContent={ completeChild } skipPartialState type='goal'>
      <FutureWhenInput error={ errors.whenField } options={ FULL_OPTIONS } />

      <FutureFieldSlider
        label='Price'
        name='purchase_price'
        mask={ maskCurrencyPositive }

        error={ errors.purchase_price }

        min={ 0 }
        max={ 100000 }
        step={ 5000 }
      />

      <InputTouchedState onChange={ setDownPaymentTouched }>
        <FutureFieldSlider
          label='Down Payment'
          name='down_payment'
          mask={ maskCurrencyPositive }

          error={ errors.down_payment }

          min={ 0 }
          max={ downPaymentRange.max }
          step={ downPaymentRange.step }
        />
      </InputTouchedState>

      <FutureFieldSlider
        label='Loan Period (months)'
        name='loan_period_months'
        mask={ maskNumberPositive }

        error={ errors.loan_period_months }
        note='The most common loan periods are 24, 36, 48, or 60 months'

        min={ 0 }
        max={ 96 }
        step={ 1 }

        isDisabled={ isLoanFieldsDisabled }
      />

      <FutureFieldButtons
        label='Interest Rate'
        name='interest_rate'
        mask={ maskPercentFromZero }
        inputMode='decimal'

        error={ errors.interest_rate }
        note='Auto loan interest rates are typically between 3% and 10%'

        min={ 0 }
        max={ 30 }
        step={ 1 }

        isDisabled={ isLoanFieldsDisabled }
      />

    </LayerStatus>
  )
}
